import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import api from '../../lib/api';

function fileToDataURL(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = (e) => resolve(String(e.target?.result || ''));
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

export default function AboutAdmin() {
  const [about, setAbout] = useState(null);
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    const r = await api.get('/about');
    // facts.lines -> single string (one line per row) for editing convenience
    const facts = (r.data.facts || []).map((f) => ({ label: f.label, linesText: (f.lines || []).join('\n') }));
    setAbout({ ...r.data, facts });
  };

  useEffect(() => { refresh(); }, []);

  const onPortraitFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 900_000) {
      toast.error('Image too large (max ~900 KB). Compress it first at squoosh.app.');
      return;
    }
    const data = await fileToDataURL(file);
    setAbout({ ...about, portrait_image: data });
    e.target.value = '';
  };

  // paragraphs helpers
  const setPara = (i, val) => {
    const paragraphs = [...about.paragraphs];
    paragraphs[i] = val;
    setAbout({ ...about, paragraphs });
  };
  const addPara = () => setAbout({ ...about, paragraphs: [...about.paragraphs, ''] });
  const removePara = (i) => setAbout({ ...about, paragraphs: about.paragraphs.filter((_, k) => k !== i) });
  const movePara = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= about.paragraphs.length) return;
    const paragraphs = [...about.paragraphs];
    [paragraphs[i], paragraphs[j]] = [paragraphs[j], paragraphs[i]];
    setAbout({ ...about, paragraphs });
  };

  // facts helpers
  const setFact = (i, key, val) => {
    const facts = [...about.facts];
    facts[i] = { ...facts[i], [key]: val };
    setAbout({ ...about, facts });
  };
  const addFact = () => setAbout({ ...about, facts: [...about.facts, { label: '', linesText: '' }] });
  const removeFact = (i) => setAbout({ ...about, facts: about.facts.filter((_, k) => k !== i) });
  const moveFact = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= about.facts.length) return;
    const facts = [...about.facts];
    [facts[i], facts[j]] = [facts[j], facts[i]];
    setAbout({ ...about, facts });
  };

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        heading_lead: about.heading_lead || '',
        heading_emph: about.heading_emph || '',
        kicker: about.kicker || '',
        location_tag: about.location_tag || '',
        paragraphs: (about.paragraphs || []).map((p) => p.trim()).filter(Boolean),
        portrait_image: about.portrait_image || '',
        portrait_alt: about.portrait_alt || '',
        portrait_caption: about.portrait_caption || '',
        facts: (about.facts || [])
          .filter((f) => (f.label || '').trim() || (f.linesText || '').trim())
          .map((f) => ({
            label: (f.label || '').trim(),
            lines: (f.linesText || '').split('\n').map((l) => l.trimEnd()).filter((l) => l.length),
          })),
      };
      await api.put('/about', payload);
      toast.success('About section updated');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setBusy(false); }
  };

  if (!about) return null;

  return (
    <section className="mb-16" data-testid="about-admin">
      <h2 className="serif text-3xl text-ink mb-6">About section</h2>

      {/* Heading + kicker */}
      <div className="border border-ink/15 p-5 mb-6">
        <div className="mono text-[10px] uppercase tracking-[0.3em] text-orchid mb-4">Heading</div>
        <div className="grid md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="mono text-[10px] uppercase tracking-[0.3em] text-ink/50 block mb-1">Heading (normal part)</label>
            <input value={about.heading_lead} onChange={(e) => setAbout({ ...about, heading_lead: e.target.value })} data-testid="about-heading-lead" placeholder="Why hello" className="w-full bg-transparent border-b border-ink/30 focus:border-orchid outline-none serif text-xl py-2" />
          </div>
          <div>
            <label className="mono text-[10px] uppercase tracking-[0.3em] text-ink/50 block mb-1">Heading (italic violet part)</label>
            <input value={about.heading_emph} onChange={(e) => setAbout({ ...about, heading_emph: e.target.value })} data-testid="about-heading-emph" placeholder="there!" className="w-full bg-transparent border-b border-ink/30 focus:border-orchid outline-none serif text-xl italic text-grape py-2" />
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="mono text-[10px] uppercase tracking-[0.3em] text-ink/50 block mb-1">Kicker (small label, top-left)</label>
            <input value={about.kicker} onChange={(e) => setAbout({ ...about, kicker: e.target.value })} placeholder="(02) — About" className="w-full bg-transparent border-b border-ink/30 focus:border-orchid outline-none mono text-sm py-2" />
          </div>
          <div>
            <label className="mono text-[10px] uppercase tracking-[0.3em] text-ink/50 block mb-1">Location tag (top-right)</label>
            <input value={about.location_tag} onChange={(e) => setAbout({ ...about, location_tag: e.target.value })} placeholder="Berlin · Montréal" className="w-full bg-transparent border-b border-ink/30 focus:border-orchid outline-none mono text-sm py-2" />
          </div>
        </div>
      </div>

      {/* Intro paragraphs */}
      <div className="border border-ink/15 p-5 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="mono text-[10px] uppercase tracking-[0.3em] text-orchid">Intro paragraphs</div>
          <button type="button" onClick={addPara} data-testid="about-add-paragraph" className="mono text-[10px] uppercase tracking-[0.2em] px-3 py-2 border border-ink/20 hover:border-orchid">+ Add paragraph</button>
        </div>
        <div className="space-y-4">
          {about.paragraphs.map((p, i) => (
            <div key={i} className="flex gap-2 items-start">
              <textarea value={p} onChange={(e) => setPara(i, e.target.value)} rows={3} data-testid={`about-paragraph-${i}`} className="flex-1 bg-transparent border border-ink/20 focus:border-orchid outline-none mono text-sm p-3 leading-relaxed" />
              <div className="flex flex-col gap-1">
                <button type="button" onClick={() => movePara(i, -1)} className="mono text-[10px] px-2 py-1 border border-ink/20 hover:border-orchid">↑</button>
                <button type="button" onClick={() => movePara(i, 1)} className="mono text-[10px] px-2 py-1 border border-ink/20 hover:border-orchid">↓</button>
                <button type="button" onClick={() => removePara(i)} className="mono text-[10px] px-2 py-1 border border-ink/20 hover:border-red-700">✕</button>
              </div>
            </div>
          ))}
          {about.paragraphs.length === 0 && <div className="mono text-xs text-ink/40">No paragraphs. Click + Add paragraph.</div>}
        </div>
      </div>

      {/* Portrait */}
      <div className="border border-ink/15 p-5 mb-6">
        <div className="mono text-[10px] uppercase tracking-[0.3em] text-orchid mb-4">Portrait photo</div>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label className="mono text-[10px] uppercase tracking-[0.3em] text-ink/50 block mb-2">Upload new photo (jpg / png / webp, ≤ 900 KB)</label>
            <input type="file" accept="image/*" onChange={onPortraitFile} data-testid="about-portrait-file" className="w-full mono text-xs" />
            <label className="mono text-[10px] uppercase tracking-[0.3em] text-ink/50 block mt-4 mb-1">…or paste an image URL</label>
            <input value={about.portrait_image?.startsWith('data:') ? '' : (about.portrait_image || '')} onChange={(e) => setAbout({ ...about, portrait_image: e.target.value })} placeholder="https://…" className="w-full bg-transparent border-b border-ink/30 focus:border-orchid outline-none mono text-xs py-2" />
            {about.portrait_image?.startsWith('data:') && <p className="mono text-[10px] text-ink/50 mt-1">Uploaded image selected (URL field cleared).</p>}
          </div>
          <div>
            {about.portrait_image ? (
              <div className="border border-ink/10 aspect-[4/5] overflow-hidden max-w-[220px]">
                <img src={about.portrait_image} alt="portrait preview" className="w-full h-full object-cover object-[center_top]" />
              </div>
            ) : (
              <div className="border border-dashed border-ink/20 aspect-[4/5] max-w-[220px] flex items-center justify-center mono text-[10px] text-ink/40">no image</div>
            )}
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-4 mt-4">
          <div>
            <label className="mono text-[10px] uppercase tracking-[0.3em] text-ink/50 block mb-1">Alt text (accessibility)</label>
            <input value={about.portrait_alt} onChange={(e) => setAbout({ ...about, portrait_alt: e.target.value })} className="w-full bg-transparent border-b border-ink/30 focus:border-orchid outline-none mono text-sm py-2" />
          </div>
          <div>
            <label className="mono text-[10px] uppercase tracking-[0.3em] text-ink/50 block mb-1">Caption (badge on photo)</label>
            <input value={about.portrait_caption} onChange={(e) => setAbout({ ...about, portrait_caption: e.target.value })} placeholder="The operator · IRL" className="w-full bg-transparent border-b border-ink/30 focus:border-orchid outline-none mono text-sm py-2" />
          </div>
        </div>
      </div>

      {/* Facts */}
      <div className="border border-ink/15 p-5 mb-6">
        <div className="flex items-center justify-between mb-2">
          <div className="mono text-[10px] uppercase tracking-[0.3em] text-orchid">Fact cards</div>
          <button type="button" onClick={addFact} data-testid="about-add-fact" className="mono text-[10px] uppercase tracking-[0.2em] px-3 py-2 border border-ink/20 hover:border-orchid">+ Add card</button>
        </div>
        <p className="mono text-[10px] text-ink/50 mb-4">Each card has a label (shown small, uppercase) and lines of text — one line per row in the box below.</p>
        <div className="grid md:grid-cols-2 gap-4">
          {about.facts.map((f, i) => (
            <div key={i} className="border border-ink/10 p-4" data-testid={`about-fact-${i}`}>
              <div className="flex items-center justify-between mb-2">
                <span className="mono text-[10px] uppercase tracking-[0.25em] text-orchid">{String(i + 1).padStart(2, '0')}</span>
                <div className="flex gap-1">
                  <button type="button" onClick={() => moveFact(i, -1)} className="mono text-[10px] px-2 py-1 border border-ink/20 hover:border-orchid">↑</button>
                  <button type="button" onClick={() => moveFact(i, 1)} className="mono text-[10px] px-2 py-1 border border-ink/20 hover:border-orchid">↓</button>
                  <button type="button" onClick={() => removeFact(i)} className="mono text-[10px] px-2 py-1 border border-ink/20 hover:border-red-700">✕</button>
                </div>
              </div>
              <input value={f.label} onChange={(e) => setFact(i, 'label', e.target.value)} placeholder="Label (e.g. Locations)" className="w-full bg-transparent border-b border-ink/30 focus:border-orchid outline-none mono text-xs uppercase tracking-[0.2em] py-2 mb-2" />
              <textarea value={f.linesText} onChange={(e) => setFact(i, 'linesText', e.target.value)} rows={3} placeholder={'Berlin, DE\nMontréal, CA\n(origin story)'} className="w-full bg-transparent border border-ink/20 focus:border-orchid outline-none serif text-lg leading-tight p-2" />
            </div>
          ))}
          {about.facts.length === 0 && <div className="mono text-xs text-ink/40">No cards. Click + Add card.</div>}
        </div>
      </div>

      <button onClick={save} disabled={busy} data-testid="about-save" className="bg-void text-paper px-6 py-3 mono uppercase tracking-[0.22em] text-[11px] hover:bg-orchid transition-colors disabled:opacity-60">
        {busy ? 'Saving…' : 'Save About section'}
      </button>
    </section>
  );
}
